import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import type {
  ApiResponse,
  BvContract,
  BvRetailContract,
  BvRetailContractFormData,
  BvRetailContractRate,
  BvRetailContractRateFormData,
  BvRateType,
  BvRateChargeUnit,
  BvDma,
  BvDmaFormData,
  BvNetworkContract,
  BvNetworkContractFormData,
  BvNetworkRate,
  BvNetworkRateFormData,
  BvEnvironmentalConfig,
  BvEnvironmentalConfigFormData,
  BvRateMode,
} from '../types/billValidationTypes';
import { auditService } from './auditService';
import { assignedCodesFromContracts, currentNetworkCode } from '../utils/networkContracts';

async function getOrgId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase
    .from('base_organisation_members')
    .select('organisation_id')
    .eq('user_id', user.id)
    .single();
  return data?.organisation_id ?? null;
}

// ─── Audit helpers ────────────────────────────────────────────────────────────
// Flatten a contract row so the history reads "rate_type stepped → smooth"
// rather than a wall of JSON, mirroring flattenSiteForAudit in siteService.
function flattenContractForAudit(row: BvContract): Record<string, unknown> {
  const d = (row.data ?? {}) as Record<string, unknown>;
  return {
    retailer_name: d.retailer_name ?? null,
    rate_type:     d.rate_type ?? null,
    source:        d.source ?? null,
    valid_from:    row.valid_from ?? null,
    valid_to:      row.valid_to ?? null,
    is_current:    row.is_current ?? null,
    notes:         row.notes ?? null,
  };
}

// The audit-worthy fields of a rate period row (bv_contract_rate columns).
const RATE_AUDIT_KEYS = [
  'period_start', 'period_end', 'peak', 'shoulder', 'off_peak', 'anytime',
  'super_peak', 'supply', 'supply_unit', 'metering', 'metering_unit',
  'supplementary_metering', 'supplementary_services',
  'feed_in_anytime', 'feed_in_peak', 'feed_in_off_peak', 'feed_in_shoulder',
] as const;
function pickRateAudit(row: Record<string, unknown> | null | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of RATE_AUDIT_KEYS) out[k] = row?.[k] ?? null;
  return out;
}

// A rate period belongs to a contract, which belongs to a site. Look the site up
// so rate-period events land on the site's History tab through listForSite.
async function siteIdForContract(contractId: string): Promise<string | null> {
  const { data } = await supabase
    .from('bv_contract').select('site_id').eq('id', contractId).single();
  return (data as { site_id?: string } | null)?.site_id ?? null;
}

// ─── Row mappers ──────────────────────────────────────────────────────────────

function mapRetail(row: BvContract, rates?: BvRetailContractRate[]): BvRetailContract {
  const d = row.data ?? {};
  return {
    id: row.id,
    org_id: row.org_id,
    site_id: row.site_id,
    retailer_id: d.retailer_id as string | undefined,
    contract_start: row.valid_from,
    contract_end: row.valid_to,
    rate_type: (d.rate_type as BvRateType) ?? 'smooth',
    source: d.source as string | undefined,
    is_current: row.is_current,
    notes: row.notes,
    created_at: row.created_at,
    updated_at: row.updated_at,
    retailer: d.retailer_name
      ? { id: (d.retailer_id as string) ?? '', name: d.retailer_name as string }
      : (row as unknown as { supplier_key?: string }).supplier_key
        ? { id: '', name: (row as unknown as { supplier_key?: string }).supplier_key! }
        : undefined,
    rates: rates ?? (d.rates as BvRetailContractRate[]) ?? [],
  };
}

// Map bv_contract_rate row → BvRetailContractRate (UI type)
type DbRate = {
  id: string;
  period_start?: string;
  period_end?: string;
  peak?: number;
  shoulder?: number;
  off_peak?: number;
  supply?: number;
  supply_unit?: string;
  metering_unit?: string;
  supp_metering_unit?: string;
  supp_services_unit?: string;
  feed_in_anytime?: number;
  feed_in_peak?: number;
  [key: string]: unknown;
};

function mapDbRate(r: DbRate): BvRetailContractRate {
  return {
    id: r.id,
    period_start: r.period_start,
    period_end: r.period_end,
    peak_rate:                   r.peak as number | undefined,
    shoulder_rate:               r.shoulder as number | undefined,
    off_peak_rate:               r.off_peak as number | undefined,
    anytime_rate:                r.anytime as number | undefined,
    super_peak_rate:             r.super_peak as number | undefined,
    supply_charge:               r.supply as number | undefined,
    supply_unit:                 r.supply_unit as BvRateChargeUnit | undefined,
    metering_rate:               r.metering as number | undefined,
    metering_unit:               r.metering_unit as BvRateChargeUnit | undefined,
    supplementary_metering_rate: r.supplementary_metering as number | undefined,
    supplementary_metering_unit: r.supp_metering_unit as BvRateChargeUnit | undefined,
    supplementary_services_rate: r.supplementary_services as number | undefined,
    supplementary_services_unit: r.supp_services_unit as BvRateChargeUnit | undefined,
    green_power_rate:            r.greenpower as number | undefined,
    feed_in_rate_anytime:        r.feed_in_anytime as number | undefined,
    feed_in_rate_peak:           r.feed_in_peak as number | undefined,
    feed_in_rate_off_peak:       r.feed_in_off_peak as number | undefined,
    feed_in_rate_shoulder:       r.feed_in_shoulder as number | undefined,
  };
}

type BvDmaRow = {
  id: string;
  org_id: string;
  site_id: string;
  dma_provider: string | null;
  start_date: string | null;
  end_date: string | null;
  created_at: string;
  updated_at: string;
};

function mapDma(row: BvDmaRow): BvDma {
  return {
    id: row.id,
    org_id: row.org_id,
    site_id: row.site_id,
    dma_provider: row.dma_provider ?? undefined,
    start_date: row.start_date ?? undefined,
    end_date: row.end_date ?? undefined,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

// Job 35: one network tariff contract — the site's dated use of one code.
//
// `supplier_key` is the code's home column (the unique index covers it); `data`
// carries a copy so a row written before that convention, and the jsonb the
// migration wrote, both still read. Falling back the other way round would let a
// stale `data.tariff_code` outvote the column the index is built on.
function mapNetwork(row: BvContract, rates?: BvNetworkRate[]): BvNetworkContract {
  const d = row.data ?? {};
  const key = (row as unknown as { supplier_key?: string | null }).supplier_key;
  return {
    id: row.id,
    org_id: row.org_id,
    site_id: row.site_id,
    tariff_code: (key ?? (d.tariff_code as string) ?? '').trim(),
    tariff_name:
      (row as unknown as { contract_name?: string | null }).contract_name
      ?? (d.tariff_name as string | undefined),
    distributor: d.distributor as string | undefined,
    valid_from: row.valid_from,
    valid_to: row.valid_to,
    rate_type: (d.rate_type as BvRateType) ?? 'stepped',
    has_demand_contract: (d.has_demand_contract as boolean) ?? false,
    open_to_new: d.open_to_new as boolean | undefined,
    is_current: row.is_current,
    source: (row as unknown as { source?: string | null }).source ?? undefined,
    notes: row.notes,
    created_at: row.created_at,
    updated_at: row.updated_at,
    rates: rates ?? [],
  };
}

// bv_contract_rate row → BvNetworkRate. Only the columns a network contract
// uses: the retail-only ones (feed-in, greenpower, metering) are left alone on
// the row rather than surfaced here, so a network card cannot present a field it
// has no way to edit.
function mapDbNetworkRate(r: DbRate): BvNetworkRate {
  return {
    id: r.id,
    period_start: r.period_start,
    period_end: r.period_end,
    peak_rate:           r.peak as number | undefined,
    shoulder_rate:       r.shoulder as number | undefined,
    off_peak_rate:       r.off_peak as number | undefined,
    anytime_rate:        r.anytime as number | undefined,
    peak_demand_rate:    r.peak_demand_rate as number | undefined,
    anytime_demand_rate: r.anytime_demand_rate as number | undefined,
    capacity_kw:         r.capacity_kw as number | undefined,
    demand_kva:          r.demand_kva as number | undefined,
    supply_charge:       r.supply as number | undefined,
    supply_unit:         r.supply_unit as BvRateChargeUnit | undefined,
  };
}

function mapEnv(row: BvContract): BvEnvironmentalConfig {
  const d = row.data;
  return {
    id: row.id,
    org_id: row.org_id,
    site_id: row.site_id,
    certificate_type: (d.certificate_type as string) ?? '',
    rate_mode: (d.rate_mode as BvRateMode) ?? 'fixed',
    period_start: row.valid_from,
    period_end: row.valid_to,
    rate: d.rate as number | undefined,
    rate_unit: d.rate_unit as string | undefined,
    notes: row.notes,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

// ============================================================================
// Retail Contracts
// ============================================================================
export const retailContractService = {
  listAll: async (): Promise<ApiResponse<BvRetailContract[]>> => {
    try {
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };

      const PAGE = 1000;

      // Paginate bv_contract
      const allContracts: BvContract[] = [];
      let page = 0;
      while (true) {
        const { data, error } = await supabase
          .from('bv_contract')
          .select('*, site:site_id(nmi, sf_site_id, addr_city, addr_state, bv_extn, client:client_id(id, name, sf_account_id))')
          .eq('org_id', orgId)
          .eq('contract_type', 'retail')
          .order('valid_from', { ascending: false, nullsFirst: false })
          .order('id')
          .range(page * PAGE, (page + 1) * PAGE - 1);
        if (error) throw error;
        allContracts.push(...((data ?? []) as BvContract[]));
        if (!data?.length || data.length < PAGE) break;
        page++;
      }

      // Paginate bv_contract_rate by org_id — avoids URL-length limit of .in(contractIds)
      const ratesByContract: Record<string, BvRetailContractRate[]> = {};
      let ratePage = 0;
      while (true) {
        const { data: rateRows } = await supabase
          .from('bv_contract_rate')
          .select('*')
          .eq('org_id', orgId)
          .order('period_start', { ascending: true, nullsFirst: false })
          .order('id')
          .range(ratePage * PAGE, (ratePage + 1) * PAGE - 1);
        for (const r of (rateRows ?? []) as DbRate[]) {
          const cid = r.contract_id as string;
          if (!ratesByContract[cid]) ratesByContract[cid] = [];
          ratesByContract[cid].push(mapDbRate(r));
        }
        if (!rateRows?.length || rateRows.length < PAGE) break;
        ratePage++;
      }

      return {
        success: true,
        data: allContracts.map((c) => ({
          ...mapRetail(c, ratesByContract[c.id]),
          _site: (c as Record<string, unknown>).site,
        })) as BvRetailContract[],
      };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  listBySite: async (siteId: string): Promise<ApiResponse<BvRetailContract[]>> => {
    try {
      const { data, error } = await supabase
        .from('bv_contract')
        .select('*')
        .eq('site_id', siteId)
        .eq('contract_type', 'retail')
        .order('valid_from', { ascending: false });
      if (error) throw error;

      const contracts = data ?? [];
      const contractIds = contracts.map(c => c.id);

      // Fetch rates from bv_contract_rate table
      let ratesByContract: Record<string, BvRetailContractRate[]> = {};
      if (contractIds.length > 0) {
        const { data: rateRows } = await supabase
          .from('bv_contract_rate')
          .select('*')
          .in('contract_id', contractIds)
          .order('period_start', { ascending: true });
        for (const r of (rateRows ?? []) as DbRate[]) {
          const cid = r.contract_id as string;
          if (!ratesByContract[cid]) ratesByContract[cid] = [];
          ratesByContract[cid].push(mapDbRate(r));
        }
      }

      return {
        success: true,
        data: contracts.map(c => mapRetail(c as BvContract, ratesByContract[c.id])),
      };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  create: async (siteId: string, form: BvRetailContractFormData): Promise<ApiResponse<BvRetailContract>> => {
    try {
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };

      let retailer_id = form.retailer_id;
      let retailer_name = form.retailer_name?.trim() ?? '';
      if (!retailer_id && retailer_name) {
        const { data: existing } = await supabase
          .from('retailer').select('id').eq('org_id', orgId).ilike('name', retailer_name).maybeSingle();
        if (existing) {
          retailer_id = existing.id;
        } else {
          const { data: created } = await supabase
            .from('retailer').insert({ org_id: orgId, name: retailer_name }).select('id').single();
          retailer_id = created?.id;
        }
      }

      const { data, error } = await supabase
        .from('bv_contract')
        .insert({
          org_id: orgId,
          site_id: siteId,
          contract_type: 'retail',
          valid_from: form.contract_start ?? null,
          valid_to: form.contract_end ?? null,
          is_current: form.is_current ?? true,
          notes: form.notes ?? null,
          data: {
            retailer_id: retailer_id ?? null,
            retailer_name,
            rate_type: form.rate_type,
            source: form.source ?? null,
            rates: [],
          },
        })
        .select()
        .single();
      if (error) throw error;

      // Audit (job 15). Fire-and-forget: a failed write never fails the save.
      void auditService.log({
        entity_type: 'contract',
        entity_id:   (data as BvContract).id,
        site_id:     siteId,
        action:      'created',
        note:        `Retail contract created${retailer_name ? ` — ${retailer_name}` : ''}`,
      });

      return { success: true, data: mapRetail(data as BvContract) };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  update: async (id: string, form: Partial<BvRetailContractFormData>): Promise<ApiResponse<BvRetailContract>> => {
    try {
      const orgId = await getOrgId();
      const { data: existing, error: fetchErr } = await supabase
        .from('bv_contract').select('*').eq('id', id).single();
      if (fetchErr) throw fetchErr;
      const row = existing as BvContract;

      let retailer_id = form.retailer_id ?? row.data.retailer_id as string | undefined;
      let retailer_name = form.retailer_name?.trim() ?? row.data.retailer_name as string ?? '';
      if (!retailer_id && form.retailer_name?.trim() && orgId) {
        const name = form.retailer_name.trim();
        const { data: exst } = await supabase
          .from('retailer').select('id').eq('org_id', orgId).ilike('name', name).maybeSingle();
        if (exst) { retailer_id = exst.id; }
        else {
          const { data: created } = await supabase
            .from('retailer').insert({ org_id: orgId, name }).select('id').single();
          retailer_id = created?.id;
          retailer_name = name;
        }
      }

      const { data, error } = await supabase
        .from('bv_contract')
        .update({
          valid_from: form.contract_start ?? row.valid_from,
          valid_to: form.contract_end ?? row.valid_to,
          is_current: form.is_current ?? row.is_current,
          notes: form.notes ?? row.notes,
          updated_at: new Date().toISOString(),
          data: {
            ...row.data,
            retailer_id: retailer_id ?? null,
            retailer_name,
            rate_type: form.rate_type ?? row.data.rate_type,
            source: form.source ?? row.data.source,
          },
        })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;

      // Audit (job 15): field-level diff on the flattened contract.
      void auditService.logFieldChanges(
        { entity_type: 'contract', entity_id: id, site_id: row.site_id ?? null },
        flattenContractForAudit(row),
        flattenContractForAudit(data as BvContract),
      );

      return { success: true, data: mapRetail(data as BvContract) };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  delete: async (id: string): Promise<ApiResponse<null>> => {
    try {
      // Capture site + retailer before the row goes, so the deletion is
      // attributable and lands on the site History tab.
      const { data: existing } = await supabase
        .from('bv_contract').select('site_id, data').eq('id', id).single();

      const { error } = await supabase.from('bv_contract').delete().eq('id', id);
      if (error) throw error;

      const retailerName = ((existing as { data?: Record<string, unknown> } | null)
        ?.data?.retailer_name as string | undefined) ?? '';
      void auditService.log({
        entity_type: 'contract',
        entity_id:   id,
        site_id:     (existing as { site_id?: string } | null)?.site_id ?? null,
        action:      'deleted',
        note:        `Retail contract deleted${retailerName ? ` — ${retailerName}` : ''}`,
      });

      return { success: true, data: null };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },
};

// ============================================================================
// Retail Contract Rates (bv_contract_rate table)
// ============================================================================
export const retailRateService = {
  create: async (contractId: string, form: BvRetailContractRateFormData): Promise<ApiResponse<BvRetailContractRate>> => {
    try {
      // Get org_id + site_id from the parent contract
      const { data: contract, error: cErr } = await supabase
        .from('bv_contract').select('org_id, site_id').eq('id', contractId).single();
      if (cErr) throw cErr;

      const { data, error } = await supabase
        .from('bv_contract_rate')
        .insert({
          org_id: contract.org_id,
          contract_id: contractId,
          period_start: form.period_start ?? null,
          period_end: form.period_end ?? null,
          peak: form.peak_rate ?? null,
          shoulder: form.shoulder_rate ?? null,
          off_peak: form.off_peak_rate ?? null,
          anytime: form.anytime_rate ?? null,
          super_peak: form.super_peak_rate ?? null,
          supply: form.supply_charge ?? null,
          supply_unit: form.supply_unit ?? null,
          metering: form.metering_rate ?? null,
          metering_unit: form.metering_unit ?? null,
          supplementary_metering: form.supplementary_metering_rate ?? null,
          supp_metering_unit: form.supplementary_metering_unit ?? null,
          supplementary_services: form.supplementary_services_rate ?? null,
          supp_services_unit: form.supplementary_services_unit ?? null,
          feed_in_anytime: form.feed_in_rate_anytime ?? null,
          feed_in_peak: form.feed_in_rate_peak ?? null,
          feed_in_off_peak: form.feed_in_rate_off_peak ?? null,
          feed_in_shoulder: form.feed_in_rate_shoulder ?? null,
          source: 'manual',
        })
        .select()
        .single();
      if (error) throw error;

      // Audit (job 15): rate period added, on the parent contract + site.
      void auditService.log({
        entity_type: 'contract',
        entity_id:   contractId,
        site_id:     (contract as { site_id?: string } | null)?.site_id ?? null,
        action:      'created',
        field:       'rate_period',
        new_value:   pickRateAudit(data as Record<string, unknown>),
        note:        `Rate period added${form.period_start ? ` (${form.period_start} → ${form.period_end ?? '—'})` : ''}`,
      });

      return { success: true, data: mapDbRate(data as DbRate) };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  update: async (rateId: string, form: BvRetailContractRateFormData): Promise<ApiResponse<BvRetailContractRate>> => {
    try {
      // Snapshot before, so the audit can diff the period against the update.
      const { data: before } = await supabase
        .from('bv_contract_rate').select('*').eq('id', rateId).single();

      const { data, error } = await supabase
        .from('bv_contract_rate')
        .update({
          period_start: form.period_start ?? null,
          period_end: form.period_end ?? null,
          peak: form.peak_rate ?? null,
          shoulder: form.shoulder_rate ?? null,
          off_peak: form.off_peak_rate ?? null,
          anytime: form.anytime_rate ?? null,
          super_peak: form.super_peak_rate ?? null,
          supply: form.supply_charge ?? null,
          supply_unit: form.supply_unit ?? null,
          metering: form.metering_rate ?? null,
          metering_unit: form.metering_unit ?? null,
          supplementary_metering: form.supplementary_metering_rate ?? null,
          supp_metering_unit: form.supplementary_metering_unit ?? null,
          supplementary_services: form.supplementary_services_rate ?? null,
          supp_services_unit: form.supplementary_services_unit ?? null,
          feed_in_anytime: form.feed_in_rate_anytime ?? null,
          feed_in_peak: form.feed_in_rate_peak ?? null,
          feed_in_off_peak: form.feed_in_rate_off_peak ?? null,
          feed_in_shoulder: form.feed_in_rate_shoulder ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', rateId)
        .select()
        .single();
      if (error) throw error;

      // Audit (job 15): field-level diff of the rate period on its contract + site.
      const contractId = (data as { contract_id?: string }).contract_id
        ?? (before as { contract_id?: string } | null)?.contract_id;
      if (contractId) {
        const siteId = await siteIdForContract(contractId);
        void auditService.logFieldChanges(
          { entity_type: 'contract', entity_id: contractId, site_id: siteId },
          pickRateAudit(before as Record<string, unknown>),
          pickRateAudit(data as Record<string, unknown>),
        );
      }

      return { success: true, data: mapDbRate(data as DbRate) };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  delete: async (contractId: string, rateId: string): Promise<ApiResponse<null>> => {
    try {
      // Capture the period being removed before it goes — the audit records it.
      const { data: removed } = await supabase
        .from('bv_contract_rate').select('*').eq('id', rateId).single();

      const { error } = await supabase.from('bv_contract_rate').delete().eq('id', rateId);
      if (error) throw error;

      const parentId = contractId
        || (removed as { contract_id?: string } | null)?.contract_id;
      if (parentId) {
        void auditService.log({
          entity_type: 'contract',
          entity_id:   parentId,
          site_id:     await siteIdForContract(parentId),
          action:      'deleted',
          field:       'rate_period',
          old_value:   pickRateAudit(removed as Record<string, unknown>),
          note:        'Rate period removed',
        });
      }

      return { success: true, data: null };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },
};

// ============================================================================
// DMA Contracts
// ============================================================================
export const dmaContractService = {
  listAll: async (): Promise<ApiResponse<(BvDma & { _site?: unknown })[]>> => {
    try {
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };

      const PAGE = 1000;
      const allRows: BvDmaRow[] = [];
      let page = 0;
      while (true) {
        const { data, error } = await supabase
          .from('bv_dma')
          .select('*, site:site_id(nmi, sf_site_id, addr_city, addr_state, bv_extn, client:client_id(id, name, sf_account_id))')
          .eq('org_id', orgId)
          .order('start_date', { ascending: false, nullsFirst: false })
          .order('id')
          .range(page * PAGE, (page + 1) * PAGE - 1);
        if (error) throw error;
        allRows.push(...((data ?? []) as BvDmaRow[]));
        if (!data?.length || data.length < PAGE) break;
        page++;
      }

      return {
        success: true,
        data: allRows.map((row) => ({
          ...mapDma(row),
          _site: (row as unknown as Record<string, unknown>).site,
        })),
      };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  listBySite: async (siteId: string): Promise<ApiResponse<BvDma[]>> => {
    try {
      const { data, error } = await supabase
        .from('bv_dma')
        .select('*')
        .eq('site_id', siteId)
        .order('start_date', { ascending: false });
      if (error) throw error;
      return { success: true, data: ((data ?? []) as BvDmaRow[]).map(mapDma) };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  create: async (siteId: string, form: BvDmaFormData): Promise<ApiResponse<BvDma>> => {
    try {
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };

      if (form.start_date && form.end_date) {
        const days = (Date.parse(form.end_date) - Date.parse(form.start_date)) / 86400000;
        if (days > 1979) return { success: false, error: 'DMA contract cannot exceed 65 months (1979 days)' };
      }

      const { data, error } = await supabase
        .from('bv_dma')
        .insert({
          org_id: orgId,
          site_id: siteId,
          dma_provider: form.dma_provider ?? null,
          start_date: form.start_date ?? null,
          end_date: form.end_date ?? null,
        })
        .select()
        .single();
      if (error) throw error;

      // Keep site.dma_provider in sync (decision C)
      if (form.dma_provider) {
        await supabase.from('site').update({ dma_provider: form.dma_provider }).eq('id', siteId);
      }
      return { success: true, data: mapDma(data as BvDmaRow) };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  update: async (id: string, form: Partial<BvDmaFormData>): Promise<ApiResponse<BvDma>> => {
    try {
      const { data: existing, error: fetchErr } = await supabase
        .from('bv_dma').select('*').eq('id', id).single();
      if (fetchErr) throw fetchErr;
      const row = existing as BvDmaRow;

      if (form.start_date && form.end_date) {
        const days = (Date.parse(form.end_date) - Date.parse(form.start_date)) / 86400000;
        if (days > 1979) return { success: false, error: 'DMA contract cannot exceed 65 months (1979 days)' };
      }

      const { data, error } = await supabase
        .from('bv_dma')
        .update({
          dma_provider: form.dma_provider ?? row.dma_provider,
          start_date: form.start_date ?? row.start_date,
          end_date: form.end_date ?? row.end_date,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;

      // Keep site.dma_provider in sync (decision C)
      if (form.dma_provider !== undefined) {
        await supabase.from('site').update({ dma_provider: form.dma_provider }).eq('id', row.site_id);
      }
      return { success: true, data: mapDma(data as BvDmaRow) };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  delete: async (id: string): Promise<ApiResponse<null>> => {
    try {
      const { error } = await supabase.from('bv_dma').delete().eq('id', id);
      if (error) throw error;
      return { success: true, data: null };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },
};

// ============================================================================
// Network Tariff Contracts (job 35)
//
// Replaces distributionConfigService. Same tables as retail
// (bv_contract + bv_contract_rate), so the site page's two cards are one
// implementation pattern rather than two shapes describing the same site.
//
// Every write also refreshes the DERIVED site columns — see syncSiteNetworkFields.
// ============================================================================

/**
 * Push the network contracts back onto the site row.
 *
 * `site.network_codes` and `site.bv_extn.network_tariff_code` are still read by
 * things this change deliberately does not touch: the NEM pricing pipeline
 * (`bv_process_distribution_usage` reads bv_extn), the sites-list RPC and its
 * export, the Salesforce sync and SiteFormPage. Job 35 was those stores
 * disagreeing with the codes on screen, so the fix is one writer keeping them
 * in step — not a fourth store.
 *
 * Best-effort by design: it runs after the contract write has already
 * succeeded, and a failure here must not present the save as failed. It is
 * logged loudly because a silent failure re-creates exactly the drift job 35
 * was about.
 */
async function syncSiteNetworkFields(siteId: string): Promise<void> {
  try {
    const { data: rows, error } = await supabase
      .from('bv_contract')
      .select('*')
      .eq('site_id', siteId)
      .eq('contract_type', 'network');
    if (error) throw error;

    const contracts = (rows ?? []).map((r) => mapNetwork(r as BvContract));
    const today = new Date().toISOString().slice(0, 10);
    const codes = assignedCodesFromContracts(contracts);
    const current = currentNetworkCode(contracts, today);

    const { data: site, error: siteErr } = await supabase
      .from('site').select('bv_extn').eq('id', siteId).single();
    if (siteErr) throw siteErr;

    const extn = { ...((site as { bv_extn?: Record<string, unknown> } | null)?.bv_extn ?? {}) };
    // Only overwritten when a contract actually covers today. With no current
    // contract the existing value stays: the pipeline keeps pricing on whatever
    // Salesforce last set rather than losing its tariff because nobody has
    // dated the cards yet.
    if (current) extn.network_tariff_code = current;

    const { error: updateErr } = await supabase
      .from('site')
      .update({ network_codes: codes, bv_extn: extn, updated_at: new Date().toISOString() })
      .eq('id', siteId);
    if (updateErr) throw updateErr;
  } catch (error: unknown) {
    console.error('[networkContractService] Failed to sync derived site fields:', error);
  }
}

/** Flatten a network contract for the audit history (job 15). */
function flattenNetworkForAudit(row: BvContract): Record<string, unknown> {
  const d = (row.data ?? {}) as Record<string, unknown>;
  return {
    tariff_code:         (row as unknown as { supplier_key?: string }).supplier_key ?? d.tariff_code ?? null,
    tariff_name:         (row as unknown as { contract_name?: string }).contract_name ?? d.tariff_name ?? null,
    distributor:         d.distributor ?? null,
    rate_type:           d.rate_type ?? null,
    has_demand_contract: d.has_demand_contract ?? null,
    valid_from:          row.valid_from ?? null,
    valid_to:            row.valid_to ?? null,
    is_current:          row.is_current ?? null,
    notes:               row.notes ?? null,
  };
}

export const networkContractService = {
  listBySite: async (siteId: string): Promise<ApiResponse<BvNetworkContract[]>> => {
    try {
      const { data, error } = await supabase
        .from('bv_contract')
        .select('*')
        .eq('site_id', siteId)
        .eq('contract_type', 'network')
        // Timeline order, oldest window first, so a site that moved
        // EA225 -> EA256 reads in the order it happened. nullsFirst puts an
        // undated assignment at the top where it is obvious it needs dating.
        .order('valid_from', { ascending: true, nullsFirst: true });
      if (error) throw error;

      const contracts = (data ?? []) as BvContract[];
      const ids = contracts.map((c) => c.id);

      const ratesByContract: Record<string, BvNetworkRate[]> = {};
      if (ids.length > 0) {
        const { data: rateRows, error: rateErr } = await supabase
          .from('bv_contract_rate')
          .select('*')
          .in('contract_id', ids)
          .order('period_start', { ascending: true });
        if (rateErr) throw rateErr;
        for (const r of (rateRows ?? []) as DbRate[]) {
          const cid = r.contract_id as string;
          if (!ratesByContract[cid]) ratesByContract[cid] = [];
          ratesByContract[cid].push(mapDbNetworkRate(r));
        }
      }

      return {
        success: true,
        data: contracts.map((c) => mapNetwork(c, ratesByContract[c.id])),
      };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  create: async (siteId: string, form: BvNetworkContractFormData): Promise<ApiResponse<BvNetworkContract>> => {
    try {
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };

      const code = form.tariff_code?.trim() ?? '';
      if (!code) return { success: false, error: 'A tariff code is required' };

      const { data, error } = await supabase
        .from('bv_contract')
        .insert({
          org_id: orgId,
          site_id: siteId,
          contract_type: 'network',
          supplier_key: code,
          contract_name: form.tariff_name?.trim() || null,
          valid_from: form.valid_from || null,
          valid_to: form.valid_to || null,
          is_current: form.is_current ?? true,
          notes: form.notes ?? null,
          source: 'manual',
          data: {
            tariff_code: code,
            tariff_name: form.tariff_name?.trim() || null,
            distributor: form.distributor?.trim() || null,
            rate_type: form.rate_type,
            has_demand_contract: form.has_demand_contract ?? false,
          },
        })
        .select()
        .single();
      if (error) throw error;

      void auditService.log({
        entity_type: 'contract',
        entity_id:   (data as BvContract).id,
        site_id:     siteId,
        action:      'created',
        note:        `Network tariff assigned — ${code}`,
      });

      await syncSiteNetworkFields(siteId);
      return { success: true, data: mapNetwork(data as BvContract) };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  update: async (id: string, form: Partial<BvNetworkContractFormData>): Promise<ApiResponse<BvNetworkContract>> => {
    try {
      const { data: existing, error: fetchErr } = await supabase
        .from('bv_contract').select('*').eq('id', id).single();
      if (fetchErr) throw fetchErr;
      const row = existing as BvContract;

      const code = form.tariff_code !== undefined
        ? form.tariff_code.trim()
        : ((row as unknown as { supplier_key?: string }).supplier_key ?? '');
      if (!code) return { success: false, error: 'A tariff code is required' };

      const name = form.tariff_name !== undefined
        ? (form.tariff_name.trim() || null)
        : ((row as unknown as { contract_name?: string | null }).contract_name ?? null);

      const { data, error } = await supabase
        .from('bv_contract')
        .update({
          supplier_key: code,
          contract_name: name,
          // `?? row.x` would make CLEARING a date impossible — a key that was
          // sent as empty is a deliberate clear, a key not sent at all is not.
          valid_from: form.valid_from !== undefined ? (form.valid_from || null) : row.valid_from,
          valid_to:   form.valid_to   !== undefined ? (form.valid_to   || null) : row.valid_to,
          is_current: form.is_current ?? row.is_current,
          notes:      form.notes ?? row.notes,
          updated_at: new Date().toISOString(),
          data: {
            ...row.data,
            tariff_code: code,
            tariff_name: name,
            ...(form.distributor !== undefined ? { distributor: form.distributor?.trim() || null } : {}),
            ...(form.rate_type !== undefined ? { rate_type: form.rate_type } : {}),
            ...(form.has_demand_contract !== undefined
              ? { has_demand_contract: form.has_demand_contract } : {}),
          },
        })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;

      void auditService.logFieldChanges(
        { entity_type: 'contract', entity_id: id, site_id: row.site_id ?? null },
        flattenNetworkForAudit(row),
        flattenNetworkForAudit(data as BvContract),
      );

      if (row.site_id) await syncSiteNetworkFields(row.site_id);
      return { success: true, data: mapNetwork(data as BvContract) };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  delete: async (id: string): Promise<ApiResponse<null>> => {
    try {
      const { data: existing } = await supabase
        .from('bv_contract').select('site_id, supplier_key, data').eq('id', id).single();

      const { error } = await supabase.from('bv_contract').delete().eq('id', id);
      if (error) throw error;

      const before = existing as
        { site_id?: string; supplier_key?: string; data?: Record<string, unknown> } | null;
      const code = before?.supplier_key ?? (before?.data?.tariff_code as string | undefined) ?? '';
      void auditService.log({
        entity_type: 'contract',
        entity_id:   id,
        site_id:     before?.site_id ?? null,
        action:      'deleted',
        note:        `Network tariff removed${code ? ` — ${code}` : ''}`,
      });

      // The code has to leave site.network_codes too, or the site keeps claiming
      // a tariff whose card is gone — the original job 35 symptom.
      if (before?.site_id) await syncSiteNetworkFields(before.site_id);
      return { success: true, data: null };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },
};

// ============================================================================
// Network Rate Periods (bv_contract_rate rows of a 'network' contract)
// ============================================================================

/** The bv_contract_rate columns a network rate period owns. */
function networkRatePayload(form: BvNetworkRateFormData): Record<string, unknown> {
  return {
    period_start: form.period_start || null,
    period_end: form.period_end || null,
    peak: form.peak_rate ?? null,
    shoulder: form.shoulder_rate ?? null,
    off_peak: form.off_peak_rate ?? null,
    anytime: form.anytime_rate ?? null,
    peak_demand_rate: form.peak_demand_rate ?? null,
    anytime_demand_rate: form.anytime_demand_rate ?? null,
    capacity_kw: form.capacity_kw ?? null,
    demand_kva: form.demand_kva ?? null,
    supply: form.supply_charge ?? null,
    supply_unit: form.supply_unit ?? null,
  };
}

export const networkRateService = {
  create: async (contractId: string, form: BvNetworkRateFormData): Promise<ApiResponse<BvNetworkRate>> => {
    try {
      const { data: contract, error: cErr } = await supabase
        .from('bv_contract').select('org_id, site_id').eq('id', contractId).single();
      if (cErr) throw cErr;

      const { data, error } = await supabase
        .from('bv_contract_rate')
        .insert({
          org_id: contract.org_id,
          contract_id: contractId,
          ...networkRatePayload(form),
          source: 'manual',
        })
        .select()
        .single();
      if (error) throw error;

      void auditService.log({
        entity_type: 'contract',
        entity_id:   contractId,
        site_id:     (contract as { site_id?: string } | null)?.site_id ?? null,
        action:      'created',
        field:       'network_rate_period',
        new_value:   pickRateAudit(data as Record<string, unknown>),
        note:        `Network rate period added${form.period_start ? ` (${form.period_start} → ${form.period_end ?? '—'})` : ''}`,
      });

      return { success: true, data: mapDbNetworkRate(data as DbRate) };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  update: async (rateId: string, form: BvNetworkRateFormData): Promise<ApiResponse<BvNetworkRate>> => {
    try {
      const { data: before } = await supabase
        .from('bv_contract_rate').select('*').eq('id', rateId).single();

      const { data, error } = await supabase
        .from('bv_contract_rate')
        .update({ ...networkRatePayload(form), updated_at: new Date().toISOString() })
        .eq('id', rateId)
        .select()
        .single();
      if (error) throw error;

      const contractId = (data as { contract_id?: string }).contract_id
        ?? (before as { contract_id?: string } | null)?.contract_id;
      if (contractId) {
        void auditService.logFieldChanges(
          { entity_type: 'contract', entity_id: contractId, site_id: await siteIdForContract(contractId) },
          pickRateAudit(before as Record<string, unknown>),
          pickRateAudit(data as Record<string, unknown>),
        );
      }

      return { success: true, data: mapDbNetworkRate(data as DbRate) };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  delete: async (contractId: string, rateId: string): Promise<ApiResponse<null>> => {
    try {
      const { data: removed } = await supabase
        .from('bv_contract_rate').select('*').eq('id', rateId).single();

      const { error } = await supabase.from('bv_contract_rate').delete().eq('id', rateId);
      if (error) throw error;

      const parentId = contractId || (removed as { contract_id?: string } | null)?.contract_id;
      if (parentId) {
        void auditService.log({
          entity_type: 'contract',
          entity_id:   parentId,
          site_id:     await siteIdForContract(parentId),
          action:      'deleted',
          field:       'network_rate_period',
          old_value:   pickRateAudit(removed as Record<string, unknown>),
          note:        'Network rate period removed',
        });
      }

      return { success: true, data: null };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },
};

// ============================================================================
// Environmental Config
// ============================================================================
export const environmentalConfigService = {
  listBySite: async (siteId: string): Promise<ApiResponse<BvEnvironmentalConfig[]>> => {
    try {
      const { data, error } = await supabase
        .from('bv_contract')
        .select('*')
        .eq('site_id', siteId)
        .eq('contract_type', 'environmental')
        .order('valid_from', { ascending: false });
      if (error) throw error;
      return { success: true, data: (data ?? []).map(mapEnv) };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  create: async (siteId: string, form: BvEnvironmentalConfigFormData): Promise<ApiResponse<BvEnvironmentalConfig>> => {
    try {
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };

      const { data, error } = await supabase
        .from('bv_contract')
        .insert({
          org_id: orgId,
          site_id: siteId,
          contract_type: 'environmental',
          valid_from: form.period_start ?? null,
          valid_to: form.period_end ?? null,
          is_current: true,
          notes: form.notes ?? null,
          data: {
            certificate_type: form.certificate_type,
            rate_mode: form.rate_mode ?? 'fixed',
            rate: form.rate ?? null,
            rate_unit: form.rate_unit ?? 'per_mwh',
          },
        })
        .select()
        .single();
      if (error) throw error;
      return { success: true, data: mapEnv(data as BvContract) };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  update: async (id: string, form: Partial<BvEnvironmentalConfigFormData>): Promise<ApiResponse<BvEnvironmentalConfig>> => {
    try {
      const { data: existing, error: fetchErr } = await supabase
        .from('bv_contract').select('*').eq('id', id).single();
      if (fetchErr) throw fetchErr;
      const row = existing as BvContract;

      const { data, error } = await supabase
        .from('bv_contract')
        .update({
          valid_from: form.period_start ?? row.valid_from,
          valid_to: form.period_end ?? row.valid_to,
          notes: form.notes ?? row.notes,
          updated_at: new Date().toISOString(),
          data: {
            ...row.data,
            ...(form.certificate_type !== undefined ? { certificate_type: form.certificate_type } : {}),
            ...(form.rate_mode !== undefined ? { rate_mode: form.rate_mode } : {}),
            ...(form.rate !== undefined ? { rate: form.rate } : {}),
            ...(form.rate_unit !== undefined ? { rate_unit: form.rate_unit } : {}),
          },
        })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return { success: true, data: mapEnv(data as BvContract) };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  delete: async (id: string): Promise<ApiResponse<null>> => {
    try {
      const { error } = await supabase.from('bv_contract').delete().eq('id', id);
      if (error) throw error;
      return { success: true, data: null };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },
};
